import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Eye,
  FileText,
  Loader2,
  Trash2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScoreBadge } from '@/features/evaluation/components/ScoreBar';
import type { Evaluation, BucketCategory } from '@/features/evaluation/types';
import {
  getBucketCategory,
  BUCKET_LABELS,
  AI_MODEL_LABELS,
} from '@/features/evaluation/types';

// =========================================
// KanbanBoard — batch results in columns
// =========================================

interface KanbanBoardProps {
  evaluations: Evaluation[];
  isLoading?: boolean;
  onDelete?: (evaluationId: string) => void;
}

interface BucketColumn {
  category: BucketCategory;
  label: string;
  evaluations: Evaluation[];
  headerColor: string;
  borderColor: string;
  bgColor: string;
  countBg: string;
}

const COLUMN_STYLES: Record<
  BucketCategory,
  { headerColor: string; borderColor: string; bgColor: string; countBg: string }
> = {
  include: {
    headerColor: 'text-emerald-800',
    borderColor: 'border-emerald-200',
    bgColor: 'bg-emerald-50/50',
    countBg: 'bg-emerald-100 text-emerald-700',
  },
  maybe: {
    headerColor: 'text-amber-800',
    borderColor: 'border-amber-200',
    bgColor: 'bg-amber-50/50',
    countBg: 'bg-amber-100 text-amber-700',
  },
  exclude: {
    headerColor: 'text-rose-800',
    borderColor: 'border-rose-200',
    bgColor: 'bg-rose-50/50',
    countBg: 'bg-rose-100 text-rose-700',
  },
};

function groupByBucket(evaluations: Evaluation[]): BucketColumn[] {
  const groups: Record<BucketCategory, Evaluation[]> = {
    include: [],
    maybe: [],
    exclude: [],
  };

  for (const evaluation of evaluations) {
    if (evaluation.status !== 'completed' || evaluation.score === null) continue;
    const bucket = getBucketCategory(evaluation.score);
    groups[bucket].push(evaluation);
  }

  // Sort each group by score (descending)
  for (const key of Object.keys(groups) as BucketCategory[]) {
    groups[key].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }

  return (['include', 'maybe', 'exclude'] as BucketCategory[]).map(
    (category) => ({
      category,
      label: BUCKET_LABELS[category],
      evaluations: groups[category],
      ...COLUMN_STYLES[category],
    })
  );
}

async function downloadBucketAsZip(
  category: BucketCategory,
  evaluations: Evaluation[]
): Promise<void> {
  const sourceIds = [...new Set(evaluations.map((e) => e.source_id))];
  if (sourceIds.length === 0) return;

  // Fetch source metadata (file_name, file_path)
  const { data: sources, error: fetchError } = await supabase
    .from('sources')
    .select('id, file_name, file_path')
    .in('id', sourceIds);

  if (fetchError || !sources) {
    throw new Error(fetchError?.message ?? 'Failed to fetch source metadata');
  }

  const zip = new JSZip();
  let filesAdded = 0;

  for (const source of sources) {
    if (!source.file_path) continue;

    const { data: blob, error: dlError } = await supabase.storage
      .from('sources')
      .download(source.file_path);

    if (dlError || !blob) continue;

    zip.file(source.file_name, blob);
    filesAdded++;
  }

  if (filesAdded === 0) {
    throw new Error('No downloadable files in this bucket');
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, `${BUCKET_LABELS[category].toLowerCase()}-sources.zip`);
}

export function KanbanBoard({
  evaluations,
  isLoading,
  onDelete,
}: KanbanBoardProps) {
  const navigate = useNavigate();
  const [downloadingBucket, setDownloadingBucket] = useState<BucketCategory | null>(null);

  async function handleDownloadBucket(category: BucketCategory, bucketEvals: Evaluation[]) {
    setDownloadingBucket(category);
    try {
      await downloadBucketAsZip(category, bucketEvals);
      toast.success(`Downloaded ${BUCKET_LABELS[category]} sources as ZIP`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingBucket(null);
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-3">
            <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
            {[1, 2].map((j) => (
              <div key={j} className="h-24 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const completedCount = evaluations.filter(
    (e) => e.status === 'completed' && e.score !== null
  ).length;

  if (completedCount === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-slate-400">
        <FileText className="mb-3 h-10 w-10" />
        <p className="text-sm font-medium">No completed evaluations</p>
        <p className="text-xs mt-1">
          Run evaluations to see results grouped by bucket
        </p>
      </div>
    );
  }

  const columns = groupByBucket(evaluations);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {columns.map((column) => (
        <KanbanColumn
          key={column.category}
          column={column}
          onView={(id) => navigate(`/results/${id}`)}
          onDelete={onDelete}
          isDownloading={downloadingBucket === column.category}
          onDownload={() =>
            handleDownloadBucket(column.category, column.evaluations)
          }
        />
      ))}
    </div>
  );
}

// =========================================
// KanbanColumn — single bucket column
// =========================================

interface KanbanColumnProps {
  column: BucketColumn;
  onView: (evaluationId: string) => void;
  onDelete?: (evaluationId: string) => void;
  isDownloading?: boolean;
  onDownload?: () => void;
}

function KanbanColumn({ column, onView, onDelete, isDownloading, onDownload }: KanbanColumnProps) {
  return (
    <div
      className={cn(
        'rounded-xl border p-3 min-h-[200px]',
        column.borderColor,
        column.bgColor
      )}
    >
      {/* Column header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-2.5 h-2.5 rounded-full',
              column.category === 'include' && 'bg-emerald-500',
              column.category === 'maybe' && 'bg-amber-500',
              column.category === 'exclude' && 'bg-rose-500'
            )}
          />
          <h3 className={cn('text-sm font-semibold', column.headerColor)}>
            {column.label}
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          {column.evaluations.length > 0 && onDownload && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={isDownloading}
                  onClick={onDownload}
                >
                  {isDownloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                  ) : (
                    <Download className="h-3.5 w-3.5 text-slate-500" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download as ZIP</TooltipContent>
            </Tooltip>
          )}
          <Badge
            variant="secondary"
            className={cn('text-xs font-mono', column.countBg)}
          >
            {column.evaluations.length}
          </Badge>
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-2">
        {column.evaluations.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-slate-300">
            <p className="text-xs">No sources</p>
          </div>
        ) : (
          column.evaluations.map((evaluation) => (
            <KanbanCard
              key={evaluation.id}
              evaluation={evaluation}
              onView={() => onView(evaluation.id)}
              onDelete={onDelete ? () => onDelete(evaluation.id) : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}

// =========================================
// KanbanCard — single evaluation card
// =========================================

interface KanbanCardProps {
  evaluation: Evaluation;
  onView: () => void;
  onDelete?: () => void;
}

function KanbanCard({ evaluation, onView, onDelete }: KanbanCardProps) {
  const score = evaluation.score ?? 0;
  const criteriaCount = evaluation.breakdown?.criteria_count ?? 0;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 bg-white"
      onClick={onView}
    >
      <CardContent className="p-3 space-y-2">
        {/* Top row: source ID + score */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 truncate">
              {evaluation.sources?.title || evaluation.sources?.file_name || evaluation.source_id.slice(0, 12) + '...'}
            </p>
            <p className="text-[11px] text-slate-400">
              {formatDistanceToNow(new Date(evaluation.created_at), {
                addSuffix: true,
              })}
            </p>
          </div>
          <ScoreBadge score={score} />
        </div>

        {/* Bottom row: metadata + actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span>{AI_MODEL_LABELS[evaluation.ai_model]}</span>
            {criteriaCount > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span>{criteriaCount} criteria</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onView}>
                  <Eye className="h-3.5 w-3.5 text-slate-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View details</TooltipContent>
            </Tooltip>

            {onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-rose-400 hover:text-rose-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete evaluation</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
